<template>
  <a-config-provider :locale="locale">
    <div class="app-container">
      <a-layout>
        <!-- 顶部导航栏，在非登录页面时显示 -->
        <a-layout-header class="header" v-if="!isLoginPage">
          <div class="logo">迈睿达</div>
          <a-menu
            :selectedKeys="selectedTopKeys"
            theme="dark"
            mode="horizontal"
            :style="{ lineHeight: '64px' }"
            @click="onTopMenuClick"
          >
            <a-menu-item key="dashboard">
              <template #icon>
                <dashboard-outlined />
              </template>
              控制台
            </a-menu-item>
            <a-menu-item key="network">
              <template #icon>
                <apartment-outlined />
              </template>
              智联网
            </a-menu-item>
            <a-menu-item key="system">
              <template #icon>
                <setting-outlined />
              </template>
              系统管理
            </a-menu-item>
            <a-menu-item key="lowcode">
              <template #icon>
                <code-outlined />
              </template>
              低代码
            </a-menu-item>
            <!-- <a-menu-item key="profile">
              <template #icon>
                <user-outlined />
              </template>
              我的物联
            </a-menu-item> -->
          </a-menu>
          <div class="header-right">
            <a-dropdown>
              <a class="user-dropdown">
                administrator
                <down-outlined />
              </a>
              <template #overlay>
                <a-menu @click="onTopMenuClick">
                  <a-menu-item key="profile">
                    <user-outlined />
                    个人中心
                  </a-menu-item>
                  <a-menu-item key="logout">
                    <logout-outlined />
                    退出登录
                  </a-menu-item>
                </a-menu>
              </template>
            </a-dropdown>
          </div>
        </a-layout-header>
        <a-layout>
          <!-- 侧边栏菜单，仅智联网页面显示且非登录页面时显示 -->
          <a-layout-sider v-if="selectedTopKeys[0] === 'network' && !isLoginPage" width="200" class="sider" :style="{background: '#fff'}">
            <a-menu
              mode="inline"
              :selectedKeys="selectedSideKeys"
              :openKeys="openKeys"
              @update:selectedKeys="onSelectSideMenu"
              @update:openKeys="(val: string[]) => openKeys = val"
              style="height: 100%; border-right: 0"
            >
              <a-menu-item key="overview">
                <template #icon><dashboard-outlined /></template>
                智联网概况
              </a-menu-item>
              <a-sub-menu key="access">
                <template #icon><setting-outlined /></template>
                <template #title>设备接入</template>
                <a-menu-item key="device-mgr">设备管理</a-menu-item>
                <a-menu-item key="model">通用物模型</a-menu-item>
                <a-menu-item key="product-mgr">产品管理</a-menu-item>
                <a-menu-item key="product-cat">产品品类</a-menu-item>
                <a-menu-item key="gateway">协议网关</a-menu-item>
                <a-menu-item key="script">协议脚本</a-menu-item>
              </a-sub-menu>
              <a-sub-menu key="ops">
                <template #icon><user-outlined /></template>
                <template #title>运维管理</template>
                <a-menu-item key="ota">ota升级</a-menu-item>
              </a-sub-menu>
            </a-menu>
          </a-layout-sider>
          <!-- 右侧内容区 -->
          <a-layout-content :class="{'login-content': isLoginPage}" :style="contentStyle">
            <router-view />
          </a-layout-content>
        </a-layout>
      </a-layout>
    </div>
  </a-config-provider>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import locale from 'ant-design-vue/es/locale/zh_CN'
import {
  DashboardOutlined,
  ApartmentOutlined,
  SettingOutlined,
  CodeOutlined,
  UserOutlined,
  LogoutOutlined,
  DownOutlined
} from '@ant-design/icons-vue'

const router = useRouter()
const route = useRoute()

const selectedTopKeys = ref(['dashboard'])
const selectedSideKeys = ref(['overview'])
const openKeys = ref(['access', 'ops'])

// 顶部菜单与路由联动
watch(
  () => route.path,
  (path) => {
    if (path === '/' || path === '') selectedTopKeys.value = ['dashboard']
    else if (path.startsWith('/network')) selectedTopKeys.value = ['network']
    else if (path.startsWith('/system')) selectedTopKeys.value = ['system']
    else if (path.startsWith('/lowcode')) selectedTopKeys.value = ['lowcode']
    else if (path.startsWith('/profile')) selectedTopKeys.value = ['profile']
    else selectedTopKeys.value = ['dashboard']

    // 侧边栏高亮和展开
    if (path === '/network/overview') {
      selectedSideKeys.value = ['overview']
      openKeys.value = []
    } else if (path === '/network/device-mgr') {
      selectedSideKeys.value = ['device-mgr']
      openKeys.value = ['access']
    } else if (path === '/network/model') {
      selectedSideKeys.value = ['model']
      openKeys.value = ['access']
    } else if (path === '/network/product-mgr') {
      selectedSideKeys.value = ['product-mgr']
      openKeys.value = ['access']
    } else if (path === '/network/product-cat') {
      selectedSideKeys.value = ['product-cat']
      openKeys.value = ['access']
    } else if (path === '/network/gateway') {
      selectedSideKeys.value = ['gateway']
      openKeys.value = ['access']
    } else if (path === '/network/script') {
      selectedSideKeys.value = ['script']
      openKeys.value = ['access']
    } else if (path === '/network/ota') {
      selectedSideKeys.value = ['ota']
      openKeys.value = ['ops']
    } else {
      selectedSideKeys.value = []
      openKeys.value = []
    }
  },
  { immediate: true }
)

function onTopMenuClick({ key }: { key: string }) {
  if (key === 'dashboard') router.push('/')
  else if (key === 'network') router.push('/network/overview')
  else if (key === 'system') router.push('/system/systemConfig/user-mgr')
  else if (key === 'lowcode') router.push('/lowcode')
  else if (key === 'profile') router.push('/profile')
  else if (key === 'logout') {
    // 清除登录状态
    localStorage.removeItem('token')
    // 跳转到登录页
    router.push('/login')
  }
}

function onSelectSideMenu(keys: string[]) {
  selectedSideKeys.value = keys
  // 路由跳转
  if (keys[0] === 'overview') router.push('/network/overview')
  else if (keys[0] === 'device-mgr') router.push('/network/device-mgr')
  else if (keys[0] === 'model') router.push('/network/model')
  else if (keys[0] === 'product-mgr') router.push('/network/product-mgr')
  else if (keys[0] === 'product-cat') router.push('/network/product-cat')
  else if (keys[0] === 'gateway') router.push('/network/gateway')
  else if (keys[0] === 'script') router.push('/network/script')
  else if (keys[0] === 'ota') router.push('/network/ota')
}

import { computed } from 'vue'

// 判断是否为登录页面
const isLoginPage = computed(() => route.path === '/login')

// 根据是否为登录页面设置内容区样式
const contentStyle = computed(() => ({
  padding: isLoginPage.value ? 0 : '24px',
  minHeight: '100vh',
  background: isLoginPage.value ? 'transparent' : '#f0f2f5'
}))
</script>

<style lang="less">
.app-container {
  min-height: 100vh;

  .header {
    display: flex;
    align-items: center;
    padding: 0 24px;

    .logo {
      color: white;
      font-size: 20px;
      font-weight: bold;
      margin-right: 24px;
      flex-shrink: 0;
    }

    .ant-menu {
      flex: 1;
      min-width: 0;
      max-width: 1100px; // 根据菜单项数量调整
      overflow-x: auto;
      background: transparent;
      border-bottom: none;
      white-space: nowrap;
    }

    .header-right {
      margin-left: auto;

      .user-dropdown {
        color: white;
        cursor: pointer;

        .anticon {
          margin-left: 8px;
        }
      }
    }
  }

  .sider {
    background: #fff;
    border-right: 1px solid #f0f0f0;
    min-height: calc(100vh - 64px);
  }

  .ant-layout-content {
    min-height: calc(100vh - 64px);
    background: #f0f2f5;
  }

  .login-content {
    padding: 0 !important;
    background: transparent !important;
  }
}
</style>